import { useMemo, useState } from 'react';
import { AssignmentDrawer, type DrawerTarget } from '../components/AssignmentDrawer';
import { LineChart, type LineSeries } from '../components/chart/LineChart';
import { colorFor } from '../components/chart/palette';
import { formatMonthKey } from '../lib/format';
import type { AggregateBundle, Filters, Meta } from '../types';

interface TechViewProps {
  stats: AggregateBundle;
  filters: Filters;
  meta: Meta | undefined;
  onFilterChange: (patch: Partial<Filters>) => void;
  /** Sätter filtret och byter till Uppdrag-fliken. */
  onShowAssignments: (patch: Partial<Filters>) => void;
}

type SortKey = 'count' | 'trend' | 'name';

const MAX_TREND_SERIES = 8;
const ALL_CATEGORIES = 'alla';

/**
 * Trendpilen jämför de senaste 30 dagarna med de 30 dagarna dessförinnan. Den
 * bär alltid både ikon och siffra – färgen ensam får inte bära betydelsen.
 */
export function TechView({ stats, filters, meta, onFilterChange, onShowAssignments }: TechViewProps) {
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [sort, setSort] = useState<SortKey>('count');
  // Målet lagras som objekt. Att bygga { tech } vid varje rendering skulle ge
  // panelen ett nytt objekt varje gång, och dess hämtningseffekt skulle löpa amok.
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);

  // "Alla kategorier" sorteras inte med i listan utan sätts först. Att försöka
  // hålla den överst via jämförelsefunktionen ger en inkonsekvent sortering –
  // den måste svara likadant oavsett vilken sida värdet står på.
  const categories = useMemo(
    () => [ALL_CATEGORIES, ...[...new Set(stats.technologies.map((t) => t.category))].sort((a, b) => a.localeCompare(b, 'sv'))],
    [stats.technologies],
  );

  const rows = useMemo(() => {
    const filtered = category === ALL_CATEGORIES ? stats.technologies : stats.technologies.filter((t) => t.category === category);
    const sorted = [...filtered];
    if (sort === 'count') sorted.sort((a, b) => b.count - a.count);
    // Tekniker utan mätbar trend sorteras sist, inte som nollor mitt i listan.
    else if (sort === 'trend')
      sorted.sort((a, b) => (b.trendPct ?? -Infinity) - (a.trendPct ?? -Infinity) || b.count - a.count);
    else sorted.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    return sorted.slice(0, 60);
  }, [stats.technologies, category, sort]);

  // Utan explicit val visar linjediagrammet de fem största teknikerna, vilket
  // är den fråga man annars klickar sig fram till varje gång.
  const trendNames = filters.tech.length > 0 ? filters.tech.slice(0, MAX_TREND_SERIES) : rows.slice(0, 5).map((t) => t.name);

  const lineSeries: LineSeries[] = trendNames.map((name, index) => ({
    key: name,
    label: name,
    color: colorFor(index),
    values: stats.months.map((monthKey) => stats.techByMonth[monthKey]?.[name] ?? 0),
  }));

  const maxCount = Math.max(1, ...rows.map((r) => r.count));

  return (
    <>
      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Teknikernas utveckling över tid</h2>
          <span className="panel__tools">{filters.tech.length > 0 ? 'Dina valda tekniker' : 'Topp 5 i urvalet'}</span>
        </div>
        <p className="panel__subtitle">
          Antal uppdrag per månad. Välj tekniker i filtret ovan för att jämföra just dem. Tidigare månader är
          underskattade: de flesta källor plockar bort uppdrag när de stängts, så bara de som fanns i arkiven finns
          kvar. Kurvorna blir jämförbara först när verktyget samlat in dagligen under hela perioden.
        </p>
        <LineChart categories={stats.months.map(formatMonthKey)} series={lineSeries} height={310} />
        {lineSeries.length > 1 ? (
          <div className="legend">
            {lineSeries.map((s) => (
              <button key={s.key} type="button" className="legend__item" onClick={() => toggleTech(s.key)}>
                <span className="legend__swatch" style={{ background: s.color }} aria-hidden="true" />
                {s.label}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      <section className="panel" style={{ marginTop: 16 }}>
        <div className="panel__header">
          <h2 className="panel__title">Alla tekniker</h2>
          <span className="panel__tools">{rows.length} av {stats.technologies.length}</span>
        </div>
        <p className="panel__subtitle">
          Klicka på en teknik för att se annonserna bakom siffran. Knappen längst till höger lägger i stället till
          tekniken som filter för hela vyn.
        </p>

        <div className="chip-row" style={{ marginBottom: 16 }}>
          {categories.map((name) => (
            <button
              key={name}
              type="button"
              className="chip"
              aria-pressed={category === name}
              onClick={() => setCategory(name)}
            >
              {name === ALL_CATEGORIES ? 'Alla kategorier' : name}
            </button>
          ))}
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="sortable" onClick={() => setSort('name')} aria-sort={sort === 'name' ? 'ascending' : 'none'}>
                  Teknik
                </th>
                <th>Kategori</th>
                <th className="num sortable" onClick={() => setSort('count')} aria-sort={sort === 'count' ? 'descending' : 'none'}>
                  Uppdrag
                </th>
                <th style={{ width: 160 }}>Andel</th>
                <th className="num sortable" onClick={() => setSort('trend')} aria-sort={sort === 'trend' ? 'descending' : 'none'}>
                  30 dagar vs föregående
                </th>
                <th style={{ width: 80 }}>
                  <span className="visually-hidden">Filtrera</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tech) => {
                const isFiltered = filters.tech.includes(tech.name);
                return (
                  <tr key={tech.name}>
                    <td>
                      <button
                        type="button"
                        className="row-link"
                        style={{ fontWeight: isFiltered ? 700 : 500 }}
                        onClick={() => setDrawerTarget({ tech: tech.name })}
                        title={`Visa de ${tech.count} uppdragen med ${tech.name}`}
                      >
                        {tech.name}
                      </button>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{tech.category}</td>
                    <td className="num">{tech.count}</td>
                    <td className="bar-cell">
                      <div className="bar-cell__fill" style={{ width: `${(tech.count / maxCount) * 100}%` }} />
                    </td>
                    <td className="num">
                      <TrendCell trendPct={tech.trendPct} currentCount={tech.currentCount} previousCount={tech.previousCount} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="chip chip--mini"
                        aria-pressed={isFiltered}
                        onClick={() => toggleTech(tech.name)}
                        title={isFiltered ? `Ta bort ${tech.name} ur filtret` : `Filtrera hela vyn på ${tech.name}`}
                      >
                        {isFiltered ? 'Filtrerad' : 'Filtrera'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 ? <p className="empty">Inga tekniker i den här kategorin för urvalet.</p> : null}
      </section>

      <AssignmentDrawer
        target={drawerTarget}
        filters={filters}
        meta={meta}
        onClose={() => setDrawerTarget(null)}
        onShowAll={onShowAssignments}
      />
    </>
  );

  function toggleTech(name: string) {
    onFilterChange({
      tech: filters.tech.includes(name) ? filters.tech.filter((t) => t !== name) : [...filters.tech, name],
    });
  }
}

/**
 * Riktningen bärs av både pil och siffra – aldrig av färgen ensam. Saknas
 * underlag visas ett streck i stället för en påhittad nolla.
 */
function TrendCell({
  trendPct,
  currentCount,
  previousCount,
}: {
  trendPct: number | null;
  currentCount: number;
  previousCount: number;
}) {
  if (trendPct === null) {
    return (
      <span className="delta delta--flat" title="För få daterade uppdrag i jämförelseperioden">
        –
      </span>
    );
  }

  const direction = trendPct > 0 ? 'up' : trendPct < 0 ? 'down' : 'flat';
  const symbol = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■';
  const label = direction === 'up' ? 'ökar' : direction === 'down' ? 'minskar' : 'oförändrat';

  return (
    <span className={`delta delta--${direction}`} title={`${currentCount} nu mot ${previousCount} föregående period`}>
      <span aria-hidden="true">{symbol}</span>
      {Math.abs(trendPct)} %<span className="visually-hidden">{label}</span>
    </span>
  );
}
